"use client";

import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { SENSITIVITY_SPECIFICITY_CALCULATOR_STRINGS } from "@/components/tools/sensitivity-specificity-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type DiagnosticResult,
  predictiveValues,
  type RatioWithInterval,
} from "@/lib/tool-kit/stats-research/diagnostic";
import { type Formatter, fixed } from "@/lib/tool-kit/stats-research/format";
import type { ProportionEstimate } from "@/lib/tool-kit/stats-research/proportions";

/*
 * Result panels of the sensitivity & specificity calculator: the full table of accuracy
 * measures from counts, and the Bayes view from sensitivity, specificity and prevalence.
 */

type Strings = typeof SENSITIVITY_SPECIFICITY_CALCULATOR_STRINGS;

const POPULATION = 10_000;
const SWEEP = [0.001, 0.01, 0.05, 0.1, 0.2, 0.5];

interface Row {
  label: string;
  estimate: string;
  interval: string;
}

function percent(fmt: Formatter, value: number): string {
  return `${fixed(fmt, value * 100, 1)}%`;
}

function proportionRow(
  label: string,
  estimate: ProportionEstimate | null,
  fmt: Formatter,
  strings: Strings
): Row {
  if (!estimate) {
    return { label, estimate: strings.notEstimable, interval: "—" };
  }
  return {
    label,
    estimate: percent(fmt, estimate.value),
    interval: `${percent(fmt, estimate.lower)} – ${percent(fmt, estimate.upper)}`,
  };
}

function ratioRow(
  label: string,
  ratio: RatioWithInterval | null,
  digits: number,
  fmt: Formatter,
  strings: Strings
): Row {
  if (!ratio) {
    return { label, estimate: strings.notEstimable, interval: "—" };
  }
  const show = (value: number) => fixed(fmt, value, digits);
  return {
    label,
    estimate: show(ratio.value),
    interval: ratio.interval
      ? `${show(ratio.interval.lower)} – ${show(ratio.interval.upper)}`
      : "—",
  };
}

function MeasureTable({ rows, head }: { rows: Row[]; head: string[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th className={`${TD} font-medium`} scope="row">
                {row.label}
              </th>
              <td className={`${TD} font-mono`}>{row.estimate}</td>
              <td className={`${TD} font-mono`}>{row.interval}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function bounds(fmt: Formatter, estimate: ProportionEstimate | null) {
  return estimate
    ? {
        value: percent(fmt, estimate.value),
        low: fixed(fmt, estimate.lower * 100, 1),
        high: `${fixed(fmt, estimate.upper * 100, 1)}%`,
      }
    : { value: "—", low: "—", high: "—" };
}

export function CountsSummary({
  result,
  cells,
  strings,
  fmt,
}: {
  result: DiagnosticResult;
  cells: { tp: number; fp: number; fn: number; tn: number };
  strings: Strings;
  fmt: Formatter;
}) {
  const rows: Row[] = [
    proportionRow(strings.sensitivity, result.sensitivity, fmt, strings),
    proportionRow(strings.specificity, result.specificity, fmt, strings),
    proportionRow(strings.ppv, result.ppv, fmt, strings),
    proportionRow(strings.npv, result.npv, fmt, strings),
    proportionRow(strings.accuracy, result.accuracy, fmt, strings),
    proportionRow(strings.prevalence, result.prevalence, fmt, strings),
    ratioRow(strings.lrPositive, result.lrPositive, 2, fmt, strings),
    ratioRow(strings.lrNegative, result.lrNegative, 2, fmt, strings),
    ratioRow(strings.dor, result.diagnosticOddsRatio, 1, fmt, strings),
  ];
  const se = bounds(fmt, result.sensitivity);
  const sp = bounds(fmt, result.specificity);
  const ppv = bounds(fmt, result.ppv);
  const npv = bounds(fmt, result.npv);
  const report = t(strings.report, {
    se: se.value,
    seLow: se.low,
    seHigh: se.high,
    sp: sp.value,
    spLow: sp.low,
    spHigh: sp.high,
    ppv: ppv.value,
    ppvLow: ppv.low,
    ppvHigh: ppv.high,
    npv: npv.value,
    npvLow: npv.low,
    npvHigh: npv.high,
  });
  const three = (value: number | undefined) =>
    value === undefined || !Number.isFinite(value) ? "—" : fixed(fmt, value, 3);
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <MeasureTable
        head={[
          strings.columnMeasure,
          strings.columnEstimate,
          strings.columnInterval,
        ]}
        rows={rows}
      />
      <StatGrid>
        <StatCard label={strings.f1} value={three(result.f1)} />
        <StatCard label={strings.youden} value={three(result.youden)} />
      </StatGrid>
      <Notice>{strings.predictiveNote}</Notice>
      <p className="text-muted-foreground text-xs">{strings.intervalNote}</p>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <p className="font-mono text-muted-foreground text-sm">{report}</p>
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workingSensitivity, {
            tp: fmt(cells.tp),
            fn: fmt(cells.fn),
            value: three(result.sensitivity?.value),
          })}
        </p>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workingSpecificity, {
            tn: fmt(cells.tn),
            fp: fmt(cells.fp),
            value: three(result.specificity?.value),
          })}
        </p>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workingPredictive, {
            tp: fmt(cells.tp),
            fp: fmt(cells.fp),
            tn: fmt(cells.tn),
            fn: fmt(cells.fn),
            ppv: three(result.ppv?.value),
            npv: three(result.npv?.value),
          })}
        </p>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workingRatios, {
            lrp: result.lrPositive
              ? fixed(fmt, result.lrPositive.value, 2)
              : strings.notEstimable,
            lrn: result.lrNegative
              ? fixed(fmt, result.lrNegative.value, 3)
              : strings.notEstimable,
          })}
        </p>
      </div>
    </section>
  );
}

export function RatesSummary({
  sensitivity,
  specificity,
  prevalence,
  strings,
  fmt,
}: {
  sensitivity: number;
  specificity: number;
  prevalence: number;
  strings: Strings;
  fmt: Formatter;
}) {
  const values = predictiveValues(
    sensitivity,
    specificity,
    prevalence,
    POPULATION
  );
  const count = (value: number) => fmt(value, { maximumFractionDigits: 1 });
  const sweep = [...new Set([...SWEEP, prevalence])].sort((a, b) => a - b);
  const lrPositive =
    specificity < 1 ? fixed(fmt, sensitivity / (1 - specificity), 2) : "∞";
  const lrNegative =
    specificity > 0 ? fixed(fmt, (1 - sensitivity) / specificity, 3) : "∞";
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.ppvLabel}
          value={percent(fmt, values.ppv)}
        />
        <StatCard label={strings.npvLabel} value={percent(fmt, values.npv)} />
        <StatCard label={strings.lrPositiveShort} value={lrPositive} />
        <StatCard label={strings.lrNegativeShort} value={lrNegative} />
      </StatGrid>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>
          {t(strings.frequencyHeading, { population: fmt(POPULATION) })}
        </h3>
        <p className="text-foreground text-sm leading-relaxed">
          {t(strings.frequencyText, {
            sick: count(values.truePositives + values.falseNegatives),
            healthy: count(values.falsePositives + values.trueNegatives),
            tp: count(values.truePositives),
            fn: count(values.falseNegatives),
            fp: count(values.falsePositives),
            tn: count(values.trueNegatives),
            positives: count(values.truePositives + values.falsePositives),
            ppv: percent(fmt, values.ppv),
          })}
        </p>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.sweepHeading}</h3>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className={TABLE}>
            <thead>
              <tr>
                <th className={TH} scope="col">
                  {strings.columnPrevalence}
                </th>
                <th className={TH} scope="col">
                  {strings.columnPpv}
                </th>
                <th className={TH} scope="col">
                  {strings.columnNpv}
                </th>
              </tr>
            </thead>
            <tbody>
              {sweep.map((p) => {
                const row = predictiveValues(sensitivity, specificity, p);
                const mine = p === prevalence;
                return (
                  <tr
                    className={mine ? "bg-primary/5 font-semibold" : ""}
                    key={p}
                  >
                    <th className={`${TD} font-medium`} scope="row">
                      {`${fmt(p * 100, { maximumFractionDigits: 2 })}%`}
                    </th>
                    <td className={`${TD} font-mono`}>
                      {percent(fmt, row.ppv)}
                    </td>
                    <td className={`${TD} font-mono`}>
                      {percent(fmt, row.npv)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-xs">{strings.sweepNote}</p>
      </div>
    </section>
  );
}
